import * as React from 'react';
import { useId, useRef } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
// @ts-ignore
import styles from '../PriceChange.module.scss?module';
import { PriceChangeRow } from '../priceChangeGroups';
import PriceTrend from '../../../../Abstracts/PriceTrend/PriceTrend';
import StatusBadge from '../../../../Abstracts/StatusBadge/StatusBadge';
import QuickOrderProduct from '../../../Index/CustomerDashboard/QuickOrder/QuickOrderProduct/QuickOrderProduct';
import { formatCurrencyEur } from '../../../../Helper/formatter';

interface PriceChangeItemProps {
  row: PriceChangeRow;
  stocked: boolean;
  open: boolean;
  onToggle: () => void;
}

/* One price change: brand and article in one line, change, the customer's
   price and a chevron on the right. The row opens the flavours of the size
   below it, with the same block as the dashboard's quick order (flavour rows,
   stock, ETA, notify, "Add to basket"), so ordering works the same in both
   places.

   No "Add" in the row (Marketing 04.11.2026, 87-318727): the row names an
   article, not a flavour, so Add would mean "open the choice" here and "into
   the basket" in the block below; Add stands only where it is settled what
   goes into the basket. The whole row toggles, which also makes it the touch
   target on phones; the chevron is the button for keyboard and screen
   readers, and its click reaches the row's handler. The name is no link, as
   a tap on it opens the row; the block below links the product page.

   On phones the name takes the first line in full width and change, price
   and chevron the second: next to the name the three left it half the width.
   From md up they stand right of the name and float instead of taking a grid
   column: a grid column keeps its width over the full height of the row, so a
   long name wrapped early although nothing stood to its right from the second
   line on. The details come first in the markup for the float. */
export default function PriceChangeItem({
  row,
  stocked,
  open,
  onToggle,
}: PriceChangeItemProps) {
  const panelId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // Clicks inside the opened block (quantities, Add to basket) keep it open.
  const handleRowClick = (event: React.MouseEvent<HTMLLIElement>) => {
    if (panelRef.current?.contains(event.target as Node)) {
      return;
    }
    onToggle();
  };

  return (
    <li
      className={clsx(styles.row, open && styles.rowOpen)}
      onClick={handleRowClick}
    >
      <div className={styles.details}>
        <PriceTrend
          trend={row.price_trend}
          percent={row.price_change_percent}
          className={styles.change}
        />
        {row.price != null && (
          <span className={styles.price}>{formatCurrencyEur(row.price)}</span>
        )}
        <button
          type="button"
          className={clsx(styles.toggle, open && styles.toggleOpen)}
          aria-expanded={open}
          aria-controls={panelId}
        >
          <FontAwesomeIcon icon={faChevronDown} aria-hidden />
          <span className="visually-hidden">{`${
            open ? 'Hide' : 'Show'
          } flavours of ${row.product_size_name}`}</span>
        </button>
      </div>
      <div className={styles.name}>
        <span className={styles.brand}>{row.brand_name}</span>{' '}
        <span className={styles.product}>{row.product_size_name}</span>
        {stocked && (
          <>
            {' '}
            <StatusBadge variant="info" className={styles.stockedBadge}>
              In your range
            </StatusBadge>
          </>
        )}
      </div>
      <div id={panelId} ref={panelRef} className={styles.panel} hidden={!open}>
        {open && (
          <QuickOrderProduct
            item={{
              product_size_erp_id: row.product_size_erp_id,
              product_name: row.product_size_name,
              brand_erp_id: row.brand_erp_id,
              brand_name: row.brand_name,
            }}
            belowRow
          />
        )}
      </div>
    </li>
  );
}
